import {
  QuestionnaireResponseService,
  ExtractedItemAnswer
} from "@zitelab/ionic-fhir-questionnaires";
import {
  IDiaprofilPatient,
  IProTask,
  DiaprofilPatient
} from "./diaprofil-patient";
import { DashboardService } from "../services/dashboard.service";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { extractConsultationTypeExt } from "../utils/extract-consultation-type-ext";
import { extractResponseValidationExt } from "../utils/extract-validation-ext";
import { extractConsultationTimeExt } from "../utils/extract-consultation-time-ext";
import {
  IConsultationStatusDetails,
  proTaskStatus,
  proTaskDetailedStatus
} from "./types";
import { calculateWho5 } from "../utils/calculate-who5";
import * as moment from "moment";
import { calculateMdi2 } from "../utils/calculate-mdi2";
import { extractQnrVersionExt } from "../utils/extract-qnr-version-ext";

export function diaprofilProTaskFactory(
  config: IProTask,
  translate: TranslateService,
  questionnaireResponseService: QuestionnaireResponseService,
  dashboard: DashboardService,
  patientConfig?: DiaprofilPatient
) {
  return new DiaprofilProTask(
    config,
    translate,
    questionnaireResponseService,
    dashboard,
    patientConfig
  );
}

@Injectable()
export class DiaprofilProTask implements IProTask {
  flattenedAnswers: {};
  flattenedDisplayAnswers: {};
  items: { [key: string]: ExtractedItemAnswer };
  response: fhir.QuestionnaireResponse;
  procedureRequest: fhir.ProcedureRequest;
  patient: DiaprofilPatient;
  task: fhir.Task;
  constructor(
    private config: IProTask,
    public translate: TranslateService,
    private questionnaireResponseService: QuestionnaireResponseService,
    private dashboard: DashboardService,
    private patientConfig: IDiaprofilPatient
  ) {
    this.patient = new DiaprofilPatient(
      patientConfig,
      translate,
      questionnaireResponseService,
      dashboard
    );
    this.task = this.config.task;
    this.procedureRequest = this.config.procedureRequest;
    this.response = this.config.response;
    this.items =
      this.response !== null
        ? this.questionnaireResponseService.extractAnswersFromQuestionnaireResponse(
            this.response
          )
        : null;
    this.flattenedAnswers =
      this.response !== null
        ? this.questionnaireResponseService.flattenQuestionnaireAnswers(
            this.items
          )
        : null;
    this.flattenedDisplayAnswers =
      this.response !== null
        ? this.questionnaireResponseService.flattenQuestionnaireAnswers(
            this.items,
            true
          )
        : null;
  }

  get consultationDate(): string {
    if (!this.task) {
      return null;
    }
    let period;
    try {
      period = this.task.restriction.period.end;
    } catch {
      period = null;
    }
    return period;
  }

  get responseDate(): string {
    if (!this.response) {
      return null;
    }
    return this.response.authored;
  }

  get daysUntilConsultation(): number {
    if (!this.consultationDate) {
      return null;
    }
    const momentObj = moment;
    const date = moment(new Date(this.consultationDate)).format("YYYY-MM-DD");
    const today = moment(new Date()).format("YYYY-MM-DD");
    const diff = moment(date).diff(moment(today), "d");
    return diff;
  }

  get consultationType(): string {
    return extractConsultationTypeExt(this.task);
  }

  get consultationTime(): string {
    const time = extractConsultationTimeExt(this.task);
    if (time !== "NOT_REGISTERED" && time !== null) {
      return time.substr(0, time.length - 3); // We are removing the seconds
    }
    return time;
  }

  get consultationStatus(): IConsultationStatusDetails {
    if (this.response === null) {
      return null;
    }
    return extractResponseValidationExt(this.response);
  }

  get consultationEvalHcp(): string {
    if (this.response === null) {
      return null;
    }
    const evalStatus = this.consultationStatus;
    return evalStatus !== null ? evalStatus.hcpInitials : null;
  }

  get who5(): { score: number; color: string } {
    if (this.response === null) {
      return null;
    }
    return calculateWho5(this.items);
  }

  get who5Score(): number {
    const who5 = this.who5;
    return who5 !== null ? who5.score : null;
  }

  get who5Color(): string {
    const who5 = this.who5;
    return who5 !== null ? who5.color : null;
  }

  get mdi2(): { score: number; color: string } {
    if (this.response === null) {
      return null;
    }
    return calculateMdi2(this.items);
  }

  get mdi2Score(): number {
    const mdi2 = this.mdi2;
    return mdi2 !== null ? mdi2.score : null;
  }

  get mdi2Color(): string {
    const mdi2 = this.mdi2;
    return mdi2 !== null ? mdi2.color : null;
  }

  get taskAuthoredDate(): string {
    return this.task.authoredOn;
  }

  get taskAuthor(): string {
    return this.task.requester.agent.display;
  }

  get patientSsn(): string {
    return this.patient.socialSecurityNumber;
  }

  get patientName(): string {
    return this.patient.fullName;
  }

  get patientAge(): number {
    return this.patient.age;
  }

  get patientSex(): "MALE" | "FEMALE" {
    return this.patient.sex;
  }

  get patientDiagnosis():
    | "UNKNOWN"
    | "DIABETES_TYPE_1"
    | "DIABETES_TYPE_2"
    | "OTHER_DIABETES_TYPE" {
    return this.patient.diagnosis;
  }

  get patientMunicipality() {
    return this.patient.municipality;
  }

  get taskStatus(): proTaskStatus {
    const daysUntilConsultation = this.daysUntilConsultation;
    if (daysUntilConsultation == null) {
      return null;
    }
    const fhirTaskStatus = this.task.status;
    switch (fhirTaskStatus) {
      case "completed":
        return "COMPLETED";
      case "cancelled":
        return "CANCELLED";
      case "failed":
        return "CANCELLED";
      case "rejected":
        return "CANCELLED";
      case "failed":
        return "CANCELLED";
      case "ready":
        if (daysUntilConsultation >= 0 && daysUntilConsultation <= 2) {
          return "CLOSE_TO_EXPIRATION";
        } else if (daysUntilConsultation < 0) {
          return "PLANNED_CONSULTATION_DATE_PASSED";
        } else {
          return "ACTIVE";
        }
    }
  }

  get taskDetailedStatus(): proTaskDetailedStatus {
    const daysUntilConsultation = this.daysUntilConsultation;
    if (daysUntilConsultation == null) {
      return null;
    }
    const fhirTaskStatus = this.task.status;
    switch (fhirTaskStatus) {
      case "completed":
        const consultationStatus = this.consultationStatus;
        if (!consultationStatus) {
          return "COMPLETED_AND_READY_FOR_CONSULTATION";
        } else {
          if (consultationStatus.handled === true) {
            if (consultationStatus.status === null) {
              // Done to handle old tasks where status was not explicitly set
              return "COMPLETED_WITH_PATIENT";
            } else {
              return consultationStatus.status;
            }
          } else if (consultationStatus.handled === false) {
            return "PATIENT_FAILED_TO_APPEAR";
          } else {
            return "COMPLETED_AND_READY_FOR_CONSULTATION";
          }
        }
      case "cancelled":
        return "CANCELLED";
      case "failed":
        return "CANCELLED";
      case "rejected":
        return "CANCELLED";
      case "failed":
        return "CANCELLED";
      case "ready":
        if (daysUntilConsultation > 0 && daysUntilConsultation <= 2) {
          return "NOT_COMPLETED_CONSULTATION_IS_SOON";
        } else if (daysUntilConsultation === 0) {
          return "NOT_COMPLETED_DAY_OF_CONSULTATION";
        } else if (daysUntilConsultation < 0) {
          return "PLANNED_CONSULTATION_DATE_PASSED";
        } else {
          return "ACTIVE";
        }
    }
  }

  get questionnaireVersion(): string {
    if (this.response === null) {
      return null;
    }
    return extractQnrVersionExt(this.response);
  }
}



// WEBPACK FOOTER //
// ./src/app/models/diaprofil-pro-task.ts